import type { CollaboratorVacationSummary } from '@/features/Collaborators/Models/collaborator.types';

export type VacationLabelTone = 'neutral' | 'success' | 'warning' | 'primary' | 'danger';

export interface VacationLabel {
    text: string;
    tone: VacationLabelTone;
}

function formatDate(date: string | null | undefined): string | null {
    if (!date) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date).trim());
    if (!match) return null;
    return `${match[3]}/${match[2]}/${match[1]}`;
}

function formatDateRange(startDate: string | null | undefined, endDate: string | null | undefined): string | null {
    if (!startDate || !endDate) return null;
    const start = formatDate(startDate);
    const end = formatDate(endDate);
    if (!start || !end) return null;
    return `${start} a ${end}`;
}

// B2: Constante genérica (sem detalhe de projeto)
// C2: Detalhe de cliente/projeto vem do campo conflictProjectName/conflictClientName
const CONFLICT_NOTE_SUFFIX = ' — conflita com';

function isFutureDate(dateStr: string | null | undefined, today: Date = new Date()): boolean {
    if (!dateStr) return false;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr).trim());
    if (!match) return false;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return date > today;
}

export function buildVacationAllocationLabel(
    summary: CollaboratorVacationSummary | null | undefined,
    context: { allocationId?: string | null; today?: Date } = {}
): VacationLabel | null {
    if (!summary || !summary.eligible) return null;

    const { allocationId, today = new Date() } = context;

    // Precedência 1: em aquisição
    if (summary.entitledFrom && isFutureDate(summary.entitledFrom, today)) {
        const entitledDate = formatDate(summary.entitledFrom);
        if (!entitledDate) return null;
        return {
            text: `em aquisição · direito a partir de ${entitledDate}`,
            tone: 'neutral',
        };
    }

    // Precedência 2-5: próxima solicitação
    // A8: Se as datas são inválidas, pula para o próximo fallback (dias vencidos, saldo)
    if (summary.nextVacation) {
        const periodText = formatDateRange(summary.nextVacation.startDate, summary.nextVacation.endDate);
        if (!periodText) {
            // Dados ruim: nextVacation existe mas datas estão inválidas.
            // Ignora e cai para os fallbacks.
        } else {

            const isConfirmed = ['Scheduled', 'OnVacation'].includes(
                summary.nextVacation.status
            );
            const isPending = ['PendingManager', 'PendingDP', 'PendingClient', 'PendingCorrection'].includes(summary.nextVacation.status);

            // Precedência 2: confirmada com conflito nesta alocação
            if (isConfirmed) {
                const hasConflictWithThisAllocation =
                    allocationId &&
                    Array.isArray(summary.nextVacation.conflictAllocationIds) &&
                    summary.nextVacation.conflictAllocationIds.includes(allocationId);

                if (hasConflictWithThisAllocation) {
                    // C2: Incluir nome do projeto se disponível
                    const conflictDetail = summary.nextVacation?.conflictProjectName
                        ? ` o ${summary.nextVacation.conflictProjectName}`
                        : ' o fim deste projeto';
                    return {
                        text: `aprovadas para ${periodText}${CONFLICT_NOTE_SUFFIX}${conflictDetail}`,
                        tone: 'warning',
                    };
                }

                // Precedência 3: confirmada sem conflito
                return {
                    text: `aprovadas para ${periodText}`,
                    tone: 'success',
                };
            }

            // Precedência 4: pendente (qualquer modalidade)
            if (isPending) {
                return {
                    text: `solicitadas para ${periodText} · aguardando aprovação`,
                    tone: 'primary',
                };
            }
        }
    }

    // Precedência 6: dias vencidos
    if (summary.expiredDays && summary.expiredDays > 0) {
        const dayText = summary.expiredDays === 1 ? 'dia' : 'dias';
        return {
            text: `sem período agendado · ${summary.expiredDays} ${dayText} vencidos`,
            tone: 'danger',
        };
    }

    // Precedência 7: saldo disponível
    if (summary.balanceDays !== undefined && summary.balanceDays !== null) {
        const dayText = summary.balanceDays === 1 ? 'dia' : 'dias';
        return {
            text: `sem período agendado · saldo de ${summary.balanceDays} ${dayText}`,
            tone: 'neutral',
        };
    }

    return null;
}
